import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type { InvoiceData } from '@/types/api';

const KEY = ['invoices'] as const;

export type InvoiceLineKind = 'service' | 'time' | 'expense' | 'item' | 'discount';

export type InvoiceLineInput = {
  kind: InvoiceLineKind;
  description: string;
  quantity: number;
  unit_cents: number;
  taxable: boolean;
  time_entry_id?: number | null;
  subscription_id?: number | null;
  expense_id?: number | null;
};

export type InvoiceInput = {
  issue_date: string;
  due_date?: string | null;
  currency?: string;
  notes?: string | null;
  tax_rate_bp: number;
  is_external?: boolean;
  external_reference?: string | null;
  line_items: InvoiceLineInput[];
};

export type ClientTaxRate = {
  rate_bp: number;
  jurisdiction: string | null;
};

/** Global admin list of invoices across all clients, with filters. */
export function useAllInvoices(
  filters: { q?: string; status?: string; client_id?: number; page?: number } = {},
) {
  return useQuery({
    queryKey: [...KEY, 'all', filters],
    queryFn: async (): Promise<Paginated<InvoiceData>> => {
      const { data } = await api.get<Paginated<InvoiceData>>('/v1/invoices', { params: filters });
      return data;
    },
  });
}

export type InvoicesSummary = {
  outstanding_cents: number;
  open_count: number;
  overdue_cents: number;
  overdue_count: number;
};

/** Portfolio totals for the admin invoices page (outstanding + overdue). */
export function useInvoicesSummary() {
  return useQuery({
    queryKey: [...KEY, 'summary'],
    queryFn: async (): Promise<InvoicesSummary> => {
      const { data } = await api.get<InvoicesSummary>('/v1/invoices/summary');
      return data;
    },
  });
}

/**
 * Destination-based WA sales tax rate for a client's on-file address, used to
 * pre-fill the invoice builder. `enabled` lets the caller defer the lookup until
 * the builder actually opens.
 */
export function useClientTaxRate(clientId: number, enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'tax-rate', clientId],
    enabled,
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<ClientTaxRate> => {
      const { data } = await api.get<ClientTaxRate>(`/v1/clients/${clientId}/tax-rate`);
      return data;
    },
  });
}

/**
 * Fetch an invoice's PDF through the authenticated API and open it in a new tab.
 * The admin PDF endpoint requires auth, so we pull it as a blob (credentialed
 * XHR) rather than a bare navigation, which would drop the session context.
 */
export async function openInvoicePdf(url: string): Promise<void> {
  const { data } = await api.get<Blob>(url, { responseType: 'blob' });
  const objectUrl = URL.createObjectURL(data);
  window.open(objectUrl, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

export function useClientInvoices(clientId: number | undefined, page = 1) {
  return useQuery({
    queryKey: clientId == null ? [...KEY, 'client', null] : [...KEY, 'client', clientId, page],
    enabled: clientId != null,
    queryFn: async (): Promise<Paginated<InvoiceData>> => {
      const { data } = await api.get<Paginated<InvoiceData>>(
        `/v1/clients/${clientId}/invoices`,
        { params: { page } },
      );
      return data;
    },
  });
}

export function useCreateInvoice(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: InvoiceInput): Promise<InvoiceData> => {
      await primeCsrf();
      const { data } = await api.post<InvoiceData>(`/v1/clients/${clientId}/invoices`, input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateInvoice(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: number; input: InvoiceInput }): Promise<InvoiceData> => {
      await primeCsrf();
      const { data } = await api.patch<InvoiceData>(`/v1/clients/${clientId}/invoices/${id}`, input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteInvoice(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${clientId}/invoices/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/**
 * Confirmation copy for deleting an invoice, shared by the client tab and the
 * cross-client list. An invoice that took money says so explicitly, since the
 * payment stays on the books after the invoice itself is gone.
 */
export function invoiceDeleteWarning(
  invoice: Pick<InvoiceData, 'status' | 'amount_paid_cents'>,
): string {
  const base =
    'It disappears from every list, the client portal, and its public pay link. The invoice number is not reused.';
  const money =
    invoice.status === 'paid' || invoice.amount_paid_cents > 0
      ? ' The payment already recorded stays on the books.'
      : '';

  return `${base}${money} This cannot be undone from the app.`;
}

/**
 * Delete from the cross-client list, where each row carries its own client and
 * a hook can't be bound to one up front.
 */
export function useDeleteAnyInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientId, id }: { clientId: number; id: number }): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${clientId}/invoices/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/**
 * Recipients chosen in the send dialog. Omitting them falls back to the
 * client's billing contact, which is what the API did before the dialog existed.
 */
export type SendInvoiceInput = {
  id: number;
  to?: string[];
  cc?: string[];
  /** The admin's wording from the composer; blank keeps the standard text. */
  subject?: string;
  message?: string;
};

/** Send (or resend) an invoice: emails a link to the public pay page. */
export function useSendInvoice(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: SendInvoiceInput): Promise<InvoiceData> => {
      await primeCsrf();
      const { data } = await api.post<InvoiceData>(`/v1/clients/${clientId}/invoices/${id}/send`, body);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Mark a draft invoice as issued without emailing anything. For invoices
 * generated and sent from another system and logged here so they can take a
 * payment.
 */
export function useIssueInvoice(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<InvoiceData> => {
      await primeCsrf();
      const { data } = await api.post<InvoiceData>(`/v1/clients/${clientId}/invoices/${id}/issue`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** Upload a source PDF (or image) onto an invoice's documents collection. */
export function useUploadInvoiceDocument(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: number; file: File }): Promise<void> => {
      await primeCsrf();
      const form = new FormData();
      form.append('file', file);
      await api.post(`/v1/clients/${clientId}/invoices/${id}/documents`, form);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** Remove a document from an invoice. */
export function useDeleteInvoiceDocument(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, mediaId }: { id: number; mediaId: number }): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${clientId}/invoices/${id}/documents/${mediaId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** Nudge the client with another copy of the invoice link. */
export function useRemindInvoice(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: SendInvoiceInput): Promise<InvoiceData> => {
      await primeCsrf();
      const { data } = await api.post<InvoiceData>(`/v1/clients/${clientId}/invoices/${id}/remind`, body);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Email the payment receipt to the client on demand. Receipts are never sent
 * automatically — this is triggered from the invoice menu after a payment settles.
 */
export function useSendInvoiceReceipt(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: SendInvoiceInput): Promise<InvoiceData> => {
      await primeCsrf();
      const { data } = await api.post<InvoiceData>(
        `/v1/clients/${clientId}/invoices/${id}/send-receipt`,
        body,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Charge the invoice's balance to the client's card on file, off-session. The
 * webhook + inline reconciliation flip the invoice to paid/partial. Errors (no
 * card, SCA required, decline) surface as the API message.
 */
export function useChargeInvoiceOnFile(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<InvoiceData> => {
      await primeCsrf();
      const { data } = await api.post<InvoiceData>(
        `/v1/clients/${clientId}/invoices/${id}/charge-on-file`,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['payments'] });
    },
  });
}

/** Revoke a client's signed card-on-file authorization for an invoice. */
export function useRevokeInvoiceAuthorization(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      invoiceId,
      authorizationId,
    }: {
      invoiceId: number;
      authorizationId: number;
    }): Promise<InvoiceData> => {
      await primeCsrf();
      const { data } = await api.delete<InvoiceData>(
        `/v1/clients/${clientId}/invoices/${invoiceId}/authorizations/${authorizationId}`,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export type RecordPaymentInput = {
  amount_cents: number;
  method: 'check' | 'cash' | 'ach' | 'other';
  paid_at?: string | null;
  reference?: string | null;
};

/** Record a payment taken outside Stripe (check, cash, ACH, other). */
export function useRecordInvoicePayment(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: number;
      input: RecordPaymentInput;
    }): Promise<InvoiceData> => {
      await primeCsrf();
      const { data } = await api.post<InvoiceData>(
        `/v1/clients/${clientId}/invoices/${id}/payments`,
        input,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/**
 * Remove a payment recorded straight onto an invoice, stepping the invoice back
 * out of paid. The server refuses Stripe charges and slices of a larger received
 * payment, so only payments this app is the record of come off this way.
 */
export function useDeleteInvoicePayment(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      invoiceId,
      paymentId,
    }: {
      invoiceId: number;
      paymentId: number;
    }): Promise<InvoiceData> => {
      await primeCsrf();
      const { data } = await api.delete<InvoiceData>(
        `/v1/clients/${clientId}/invoices/${invoiceId}/payments/${paymentId}`,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      // The reversal also takes back the timeline entry the payment wrote.
      qc.invalidateQueries({ queryKey: ['timeline', clientId] });
    },
  });
}

/** Void an unpaid invoice and release any time or expenses it reserved. */
export function useVoidInvoice(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<InvoiceData> => {
      await primeCsrf();
      const { data } = await api.post<InvoiceData>(`/v1/clients/${clientId}/invoices/${id}/void`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
